import {Box, Flex, IconButton, Portal, Text} from '@chakra-ui/react';
import {
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  useDisclosure,
} from '@chakra-ui/react';
import {useLocation, useNavigate} from 'react-router-dom';
import {
  TablerBook,
  TablerClipboard,
  TablerDotsVertical,
  TablerHome,
  TablerLogout,
  TablerShield,
  TablerTool,
  TablerUser,
} from '../icons';
import {useAuth} from '../contexts/AuthContext';
import {useUser} from '../contexts/UserContext';
import {useChartingQueueCount} from '../hooks/useChartingQueue';
import {useEhrLinked} from '../hooks/useEhrLinked';
import {
  APP_SHELL_TABS,
  buildSidebarResetState,
  getAdminAppShellRoute,
  getAppShellActiveTab,
  isAdminAppRoute,
} from './appShellRoutes';
import ProfileModal from './ProfileModal';
import SignOutConfirmModal from './common/SignOutConfirmModal';

const TAB_ICONS: Record<string, any> = {
  home: TablerHome,
  charting: TablerClipboard,
  tools: TablerTool,
  learn: TablerBook,
};

const MobileTabButton = ({
  badgeCount = 0,
  icon: TabIcon,
  isActive,
  label,
  onClick,
}: Record<string, any>) => {
  const badgeLabel = badgeCount > 9 ? '9+' : String(badgeCount);

  return (
    <Flex
      as="button"
      type="button"
      aria-label={badgeCount > 0 ? `${label} (${badgeCount} pending)` : label}
      aria-current={isActive ? 'page' : undefined}
      direction="column"
      align="center"
      justify="center"
      gap="2px"
      flex="1 1 0"
      minW={0}
      h="60px"
      color="white"
      borderRadius="lg"
      bg={isActive ? 'whiteAlpha.300' : 'transparent'}
      cursor="pointer"
      sx={{touchAction: 'manipulation'}}
      onClick={onClick}
      _hover={{bg: isActive ? 'whiteAlpha.400' : 'whiteAlpha.200'}}
      _active={{bg: isActive ? 'whiteAlpha.400' : 'whiteAlpha.200'}}
      _focusVisible={{
        outline: 'none',
        boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.75)',
      }}
    >
      <Box position="relative" lineHeight={0}>
        <TabIcon size={20} aria-hidden="true" />
        {badgeCount > 0 && (
          <Text
            as="span"
            data-testid="mobile-nav-badge"
            position="absolute"
            top="-7px"
            right="-10px"
            minW="16px"
            h="16px"
            px="4px"
            borderRadius="full"
            bg="white"
            color="opalite.700"
            fontSize="10px"
            fontWeight="bold"
            lineHeight="16px"
            textAlign="center"
            boxShadow="0 0 0 2px var(--chakra-colors-opalite-600)"
            pointerEvents="none"
          >
            {badgeLabel}
          </Text>
        )}
      </Box>
      <Text
        as="span"
        maxW="100%"
        px={1}
        fontSize="2xs"
        fontWeight="medium"
        lineHeight="1.15"
        noOfLines={1}
      >
        {label}
      </Text>
    </Flex>
  );
};

// iOS-style workspace chrome for phones and portrait/small tablets. Primary
// destinations stay under the user's thumb; the desktop rail takes over only
// at `lg`, where the planner also becomes a true master/detail workspace.
const MobileTopNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {logout} = useAuth();
  const {userData, mockEhrMode} = useUser();
  const {
    isOpen: isProfileOpen,
    onOpen: onProfileOpen,
    onClose: onProfileClose,
  } = useDisclosure();
  const {
    isOpen: isSignOutOpen,
    onOpen: onSignOutOpen,
    onClose: onSignOutClose,
  } = useDisclosure();

  const activeTab = getAppShellActiveTab(location.pathname);
  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';
  const ehrLinked = useEhrLinked();
  const chartingPendingCount = useChartingQueueCount(
    ehrLinked,
    userData?.role === 'superadmin' && mockEhrMode,
  );
  const visibleTabs = APP_SHELL_TABS.filter(
    tab => tab.id !== 'charting' || ehrLinked,
  );

  const navigateToPage = (route: string) => {
    navigate(route, {state: buildSidebarResetState()});
  };

  return (
    <>
      <Flex
        as="nav"
        aria-label="Primary navigation"
        data-testid="mobile-top-nav"
        data-placement="bottom"
        display={{base: 'flex', lg: 'none'}}
        align="flex-start"
        gap={1}
        flexShrink={0}
        bg="opalite.600"
        color="white"
        px={2}
        pt={1}
        sx={{
          minHeight: 'calc(72px + env(safe-area-inset-bottom, 0px))',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          boxShadow: '0 -6px 16px -12px rgba(15, 23, 42, 0.7)',
        }}
      >
        {visibleTabs.map(tab => {
          const TabIcon = TAB_ICONS[tab.id];
          return (
            <MobileTabButton
              key={tab.id}
              icon={TabIcon}
              label={tab.label}
              isActive={activeTab === tab.id && !isProfileOpen}
              badgeCount={tab.id === 'charting' ? chartingPendingCount : 0}
              onClick={() => navigateToPage(tab.route)}
            />
          );
        })}

        <MobileTabButton
          icon={TablerUser}
          label="Profile"
          isActive={isProfileOpen}
          onClick={onProfileOpen}
        />

        <Menu placement="top-end" autoSelect={false}>
          <MenuButton
            as={IconButton}
            aria-label="Account and more"
            variant="ghost"
            color="white"
            h="60px"
            minW="44px"
            flex="0 0 44px"
            borderRadius="lg"
            bg={
              isAdminAppRoute(location.pathname)
                ? 'whiteAlpha.300'
                : 'transparent'
            }
            _hover={{bg: 'whiteAlpha.200'}}
            _active={{bg: 'whiteAlpha.300'}}
            _focusVisible={{
              outline: 'none',
              boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.75)',
            }}
            icon={<TablerDotsVertical size={22} aria-hidden="true" />}
          />
          <Portal>
            <MenuList zIndex="popover" minW="11rem">
              {isAdmin && (
                <MenuItem
                  icon={<TablerShield size={18} aria-hidden="true" />}
                  onClick={() =>
                    navigateToPage(getAdminAppShellRoute(userData?.role))
                  }
                >
                  Admin
                </MenuItem>
              )}
              {isAdmin && <MenuDivider />}
              <MenuItem
                icon={<TablerLogout size={18} aria-hidden="true" />}
                onClick={onSignOutOpen}
              >
                Sign Out
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      </Flex>
      <ProfileModal isOpen={isProfileOpen} onClose={onProfileClose} />
      <SignOutConfirmModal
        isOpen={isSignOutOpen}
        onClose={onSignOutClose}
        onConfirm={logout}
      />
    </>
  );
};

export default MobileTopNav;
